import React from 'react'
import { useNavigate } from 'react-router'
import { Button, ThemeSwitcher } from '@/components/ui'
import { useTheme } from '@/hooks/useTheme'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/utils/cn'
import { getLogger } from '@/services/shared/LoggerFactory'
import logoUrl from '@/assets/bitewise-logo.svg'

export interface HeaderProps {
  className?: string
  onMenuToggle?: () => void
  showMenuButton?: boolean
}

const Header: React.FC<HeaderProps> = ({
  className,
  onMenuToggle,
  showMenuButton = true
}) => {
  const logger = getLogger();
  const { isDark } = useTheme()
  const { isAuthenticated, signOut, isLoading, user } = useAuth()
  const navigate = useNavigate()
  const isPatient = !!user && 'role' in user && user.role === 'patient'

  const handleSignOut = async () => {
    try {
      await signOut()
      // Clear navigation history and redirect to home
      window.history.replaceState(null, '', '/')
      window.location.href = '/'
    } catch (error) {
      logger.error('Sign out error:', error)
    }
  }

  const handleSignIn = () => {
    navigate('/login')
  }

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
    logger.debug('isAuthenticated: ' + isAuthenticated);
    if (isAuthenticated) {
      navigate('/dashboard')
    } else {
      navigate('/')
    }
  }

  const handleAboutClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    navigate('/about')
  }

  const handleSupportClick = () => {
    window.location.href = 'mailto:support@bitewise.com.au'
  }

  const handleShoppingListClick = () => {
    navigate('/shopping-list')
  }

  const ShoppingCartIcon = () => (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6 6h15l-1.5 9H8L6 3H3"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="19" r="1.25" fill="currentColor" />
      <circle cx="17" cy="19" r="1.25" fill="currentColor" />
    </svg>
  )

  return (
    <header className={cn(
      'fixed top-4 left-1/2 transform -translate-x-1/2 z-50',
      'w-full max-w-7xl px-4',
      className
    )}
    >
      {/* Rounded container with backdrop blur */}
      <div className={cn(
        'rounded-full px-6 py-3 backdrop-blur-md border border-white/10',
        'shadow-lg transition-all duration-300',
        isDark ? 'bg-secondary-900/95' : 'bg-secondary-500/95'
      )}>
        <div className="flex items-center justify-between">
          {/* Left side - Logo */}
          <div className="flex items-center gap-4">
            {showMenuButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onMenuToggle}
                className={cn(
                  'lg:hidden rounded-full p-2',
                  'text-white',
                  isDark ? 'hover:bg-white/15' : 'hover:bg-white/10'
                )}
                aria-label="Toggle menu"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </Button>
            )}

            {/* BiteWise Logo */}
            <div className="flex items-center">
              <button
                onClick={handleHomeClick}
                className={cn(
                  'rounded-lg p-1 transition-colors duration-200',
                  'hover:bg-white/10 focus:bg-white/10',
                  'focus:outline-none focus:ring-2 focus:ring-white/20'
                )}
                aria-label="Go to home page"
              >
                <img
                  src={logoUrl}
                  alt="BiteWise"
                  className="h-7 w-auto"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          {/* Center/Right side - Navigation and Theme toggle */}
          <div className="flex items-center space-x-6">
            {/* Navigation Links */}
            <nav className="hidden lg:flex items-center space-x-6">
              <a
                href="/about"
                onClick={handleAboutClick}
                className={cn(
                  'font-medium transition-colors duration-200 px-3 py-1.5 rounded-full',
                  'text-white',
                  'hover:text-secondary-200',
                  'hover:bg-white/10'
                )}
                title="About Bitewise"
                aria-label="About Bitewise"
              >
                About
              </a>
              <button
                onClick={handleSupportClick}
                className={cn(
                  'font-medium transition-colors duration-200 px-3 py-1.5 rounded-full',
                  'text-white',
                  'hover:text-secondary-200',
                  'hover:bg-white/10'
                )}
                aria-label="Contact Support"
                title="Contact Support"
              >
                Support
              </button>
              {isPatient && (
                <button
                  onClick={handleShoppingListClick}
                  className={cn(
                    'transition-colors duration-200 rounded-full p-2',
                    'text-white',
                    isDark ? 'hover:bg-white/15' : 'hover:bg-white/10'
                  )}
                  aria-label="Shopping List"
                  title="Shopping List"
                >
                  <ShoppingCartIcon />
                </button>
              )}
            </nav>

            {/* Authentication Section */}
            <div className="flex items-center space-x-4">
              {/* Hide authentication buttons on mobile, show only on desktop */}
              {isAuthenticated ? (
                <div className="hidden lg:flex items-center space-x-3" data-testid="user-menu">
                  <Button
                    variant="ghost"
                    onClick={handleSignOut}
                    disabled={isLoading}
                    className={cn(
                      'text-white rounded-full px-4 py-2',
                      isDark ? 'hover:bg-white/15' : 'hover:bg-white/10'
                    )}
                    data-testid="logout-button"
                  >
                    {isLoading ? 'Signing out...' : 'Sign Out'}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="md"
                  onClick={handleSignIn}
                  className={cn(
                    'hidden lg:flex text-white rounded-full px-4 py-2',
                    isDark ? 'hover:bg-white/15' : 'hover:bg-white/10'
                  )}
                >
                  Login
                </Button>
              )}

              {/* Theme Toggle Button */}
              <ThemeSwitcher
                variant="mobile"
                size="sm"
                showLabel={false}
              />
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export { Header }